import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Header } from "@/components/header";
import { SUPPORT_EMAIL } from "@/lib/subiza";

export const Route = createFileRoute("/terms")({
  head: () => ({
    meta: [
      { title: "Terms and Conditions — Subiza" },
      {
        name: "description",
        content:
          "The terms that govern how teachers and students may use Subiza, including what the AI question generator can and cannot be relied on for.",
      },
    ],
  }),
  component: TermsPage,
});

const TOC = [
  { id: "s1", n: "1", label: "Agreeing to these terms" },
  { id: "s2", n: "2", label: "Teacher accounts" },
  { id: "s3", n: "3", label: "Exams, PINs and students" },
  { id: "s4", n: "4", label: "AI question generation" },
  { id: "s5", n: "5", label: "Your content" },
  { id: "s6", n: "6", label: "Answers and results" },
  { id: "s7", n: "7", label: "Acceptable use" },
  { id: "s8", n: "8", label: "Data and privacy" },
  { id: "s9", n: "9", label: "Availability and limits" },
  { id: "s10", n: "10", label: "Suspension and ending" },
  { id: "s11", n: "11", label: "Disclaimers and liability" },
  { id: "s12", n: "12", label: "Changes to these terms" },
  { id: "s13", n: "13", label: "Law and contact" },
];

function tocLinkClass(active) {
  return `grid grid-cols-[2.2ch_minmax(0,1fr)] gap-2.5 -ml-px border-l-2 py-1.5 pl-3.5 text-sm leading-snug text-muted transition-colors hover:text-text ${
    active ? "border-l-primary font-semibold text-text" : "border-l-transparent"
  }`;
}

function TocLinks({ activeId }) {
  return (
    <ol className="border-l border-border">
      {TOC.map((item) => (
        <li key={item.id}>
          <a
            href={`#${item.id}`}
            aria-current={activeId === item.id ? "true" : undefined}
            className={tocLinkClass(activeId === item.id)}
          >
            <span className="font-mono text-xs leading-6">{item.n}</span>
            <span>{item.label}</span>
          </a>
        </li>
      ))}
    </ol>
  );
}

function TermsPage() {
  const [activeId, setActiveId] = useState("");

  // Scroll-spy: highlight the section currently under the sticky header in both
  // the rail and the mobile contents popover, matching the standalone document
  // this page was converted from.
  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return;
    const sections = document.querySelectorAll("[data-term-section]");
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveId(entry.target.id);
        });
      },
      { rootMargin: "-15% 0px -75% 0px" },
    );
    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen bg-transparent relative isolate">
      <Header />
      <main className="container mx-auto scroll-smooth px-4 py-12 lg:py-16 relative z-10">
        <div id="top" className="mx-auto max-w-6xl">
          {/* Top bar */}
          <div className="flex items-baseline justify-between gap-4 border-b border-border pb-5">
            <span className="font-display text-2xl font-bold tracking-tight">Subiza</span>
            <span className="font-mono text-xs uppercase tracking-widest text-muted">
              Terms and Conditions
            </span>
          </div>

          {/* Hero */}
          <div className="py-12 lg:py-14">
            <p className="mb-4 font-mono text-sm text-muted">
              Effective 9 October 2026 &middot; Version 1.0 &middot; Applies to teachers and
              students
            </p>
            <h1 className="font-display text-4xl font-bold tracking-tight text-balance lg:text-5xl">
              Terms and Conditions
            </h1>
            <p className="mt-3 max-w-2xl text-lg leading-relaxed text-muted">
              These terms explain how teachers and students may use Subiza, what the AI question
              generator can and cannot be relied on for, and who is responsible for what. Please
              read them before you create or join an exam.
            </p>
          </div>

          <div className="grid items-start gap-8 lg:grid-cols-[220px_minmax(0,1fr)] lg:gap-16">
            {/* Contents rail (desktop) */}
            <nav
              aria-label="Contents"
              className="sticky top-24 hidden max-h-[calc(100vh-7rem)] overflow-y-auto lg:block"
            >
              <p className="mb-3 font-mono text-xs uppercase tracking-widest text-muted">
                Contents
              </p>
              <TocLinks activeId={activeId} />
            </nav>

            {/* Mobile contents popover */}
            <details className="mb-7 rounded-xl border border-border bg-surface/80 backdrop-blur-sm lg:hidden">
              <summary className="flex cursor-pointer list-none items-center justify-between px-4 py-3.5 font-mono text-xs uppercase tracking-widest text-muted marker:hidden [&::-webkit-details-marker]:hidden">
                Contents
                <span className="text-base leading-none text-muted" aria-hidden="true">
                  +
                </span>
              </summary>
              <div className="px-4 pb-3">
                <TocLinks activeId={activeId} />
              </div>
            </details>

            <article className="min-w-0">
              {/* Plain-language summary */}
              <aside className="mb-2 rounded-2xl bg-[color-mix(in_oklab,var(--color-primary)_8%,var(--color-card))] p-6 lg:p-8">
                <h2 className="mb-4 font-mono text-xs uppercase tracking-widest text-accent">
                  The short version
                </h2>
                <ul className="space-y-3.5">
                  {[
                    "Teachers own their exams and notes, and are responsible for what they upload and publish.",
                    "AI writes a first draft. You review every question before students see it, because AI can be wrong.",
                    "Students join with a six-digit PIN. Share it only with the people who should sit the exam.",
                    "Notes you use for AI generation are sent to Google's Gemini service.",
                    "Do not cheat, share exams without permission, or interfere with the service.",
                    "We may change or pause features, and our liability is limited as set out in section 11.",
                  ].map((line) => (
                    <li key={line} className="grid grid-cols-[18px_minmax(0,1fr)] gap-3.5">
                      <span
                        className="mt-1.5 size-3 rounded-full bg-accent ring-4 ring-accent/20"
                        aria-hidden="true"
                      />
                      <span className="leading-relaxed">{line}</span>
                    </li>
                  ))}
                </ul>
                <p className="mt-4 text-sm text-muted">
                  This summary is for convenience. The full terms below are what apply.
                </p>
              </aside>

              {/* Section 1 */}
              <section
                id="s1"
                data-term-section
                className="mt-8 scroll-mt-24 border-t border-border pt-10"
              >
                <h2 className="flex items-baseline gap-3.5 font-display text-2xl font-bold tracking-tight text-balance">
                  <span className="min-w-[2.2ch] font-mono text-sm text-accent">1</span>
                  Agreeing to these terms
                </h2>
                <p className="mb-4 mt-4 max-w-[66ch] leading-relaxed">
                  By creating a teacher account, creating an exam, or joining an exam with a PIN,
                  you agree to these terms. If you do not agree, please do not use Subiza.
                </p>
                <p className="mb-4 max-w-[66ch] leading-relaxed">
                  If you use Subiza for a school or organization, you confirm that you have the
                  authority to accept these terms on its behalf.
                </p>
                <p className="mb-4 max-w-[66ch] leading-relaxed">
                  Students under 18 should use Subiza only with the knowledge of their teacher or
                  school. The teacher or school running the exam is responsible for any permission
                  needed from parents or guardians.
                </p>
              </section>

              {/* Section 2 */}
              <section
                id="s2"
                data-term-section
                className="mt-8 scroll-mt-24 border-t border-border pt-10"
              >
                <h2 className="flex items-baseline gap-3.5 font-display text-2xl font-bold tracking-tight text-balance">
                  <span className="min-w-[2.2ch] font-mono text-sm text-accent">2</span>
                  Teacher accounts
                </h2>
                <p className="mb-4 mt-4 max-w-[66ch] leading-relaxed">
                  Teachers register for an account and log in to reach their dashboard, where they
                  create and manage exams. You must give accurate details and keep your login
                  secure.
                </p>
                <p className="mb-4 max-w-[66ch] leading-relaxed">
                  You are responsible for everything done through your account. Do not share your
                  login with students or colleagues, and tell us promptly if you think someone else
                  has used it.
                </p>
              </section>

              {/* Section 3 */}
              <section
                id="s3"
                data-term-section
                className="mt-8 scroll-mt-24 border-t border-border pt-10"
              >
                <h2 className="flex items-baseline gap-3.5 font-display text-2xl font-bold tracking-tight text-balance">
                  <span className="min-w-[2.2ch] font-mono text-sm text-accent">3</span>
                  Exams, PINs and students
                </h2>
                <p className="mb-4 mt-4 max-w-[66ch] leading-relaxed">
                  Each exam session has a six-digit PIN. Students enter the PIN to join the exam, so
                  the teacher does not need to send questions to each student.
                </p>
                <div
                  aria-label="Example PIN: 4 8 2 9 1 7"
                  className="mb-2 mt-5 flex flex-wrap gap-2"
                >
                  {["4", "8", "2", "9", "1", "7"].map((digit) => (
                    <span
                      key={digit}
                      className="grid size-11 place-items-center rounded-lg border-2 border-accent bg-surface/80 backdrop-blur-sm font-mono text-2xl font-medium"
                    >
                      {digit}
                    </span>
                  ))}
                </div>
                <p className="mb-6 text-sm text-muted">Example of the six-digit format.</p>
                <p className="mb-4 max-w-[66ch] leading-relaxed">
                  Anyone who has the PIN can try to join, so the teacher decides who receives it and
                  when. Share it only with the students meant to sit that exam.
                </p>
                <p className="mb-4 max-w-[66ch] leading-relaxed">
                  Depending on how access is set up, students may not need an account. A student who
                  joins an exam agrees to these terms for that session.
                </p>
                <p className="mb-4 max-w-[66ch] leading-relaxed">
                  Subiza keeps the time for each exam. Submit your answers before time runs out,
                  because answers not submitted when the session ends may not be counted. Teachers
                  should tell students in advance how late or interrupted attempts will be handled.
                </p>
              </section>

              {/* Section 4 */}
              <section
                id="s4"
                data-term-section
                className="mt-8 scroll-mt-24 border-t border-border pt-10"
              >
                <h2 className="flex items-baseline gap-3.5 font-display text-2xl font-bold tracking-tight text-balance">
                  <span className="min-w-[2.2ch] font-mono text-sm text-accent">4</span>
                  AI question generation
                </h2>
                <p className="mb-4 mt-4 max-w-[66ch] leading-relaxed">
                  Teachers can paste notes or upload PDF or DOCX files and ask Subiza to draft
                  questions. You choose the question types (multiple choice, true or false, fill in
                  the blank, matching), the difficulty, the students' grade or class level, and any
                  extra instructions. Subiza sends this material to Google's Gemini AI, which writes
                  the draft.
                </p>
                <p className="mb-4 max-w-[66ch] leading-relaxed">
                  <strong>AI can be wrong.</strong> A draft question may contain a factual error, a
                  wrong answer, unclear wording, or a level that does not suit your class. Teachers
                  must review, edit and approve every question before publishing an exam, so that
                  students are never the first to find a mistake.
                </p>
                <div className="my-7 grid gap-4 sm:grid-cols-2">
                  <div className="rounded-xl border border-border bg-surface/80 backdrop-blur-sm p-5">
                    <h3 className="mb-3.5 font-mono text-xs uppercase tracking-widest text-muted">
                      Subiza checks for you
                    </h3>
                    <ul className="space-y-2.5 text-[15px] leading-normal">
                      {[
                        "Removes duplicate questions",
                        "Makes sure the correct answer matches one of the listed options",
                        "Drops questions whose answer cannot be matched",
                        "Keeps only the question types you asked for",
                        "Keeps the format the exam player expects",
                      ].map((line) => (
                        <li key={line} className="relative pl-5">
                          <span
                            className="absolute left-0 top-2 size-1.5 rounded-full bg-accent"
                            aria-hidden="true"
                          />
                          {line}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="rounded-xl border border-transparent bg-[color-mix(in_oklab,var(--color-primary)_8%,var(--color-card))] p-5">
                    <h3 className="mb-3.5 font-mono text-xs uppercase tracking-widest text-accent">
                      Only you can check
                    </h3>
                    <ul className="space-y-2.5 text-[15px] leading-normal">
                      {[
                        "Whether the facts are correct",
                        "Whether the answer key is right",
                        "Whether the level suits your students",
                        "Whether the wording is fair and clear",
                        "Whether the exam covers what you taught",
                      ].map((line) => (
                        <li key={line} className="relative pl-5">
                          <span
                            className="absolute left-0 top-2 size-1.5 rounded-full bg-accent"
                            aria-hidden="true"
                          />
                          {line}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
                <p className="mb-4 max-w-[66ch] leading-relaxed">
                  Because questions with unmatched answers are dropped, you may receive fewer
                  questions than you asked for. We do not guarantee that generated questions are
                  accurate, original, or suited to a particular curriculum.
                </p>
              </section>

              {/* Section 5 */}
              <section
                id="s5"
                data-term-section
                className="mt-8 scroll-mt-24 border-t border-border pt-10"
              >
                <h2 className="flex items-baseline gap-3.5 font-display text-2xl font-bold tracking-tight text-balance">
                  <span className="min-w-[2.2ch] font-mono text-sm text-accent">5</span>
                  Your content
                </h2>
                <p className="mb-4 mt-4 max-w-[66ch] leading-relaxed">
                  Your content means the notes, documents, questions, answers and exams you add to
                  Subiza.
                </p>
                <p className="mb-4 max-w-[66ch] leading-relaxed">
                  You keep ownership of your content. You give Subiza permission to store it,
                  process it, show it to the students you invite, and send notes to Gemini for
                  question generation. We use this permission only to run the service for you.
                </p>
                <ul className="mb-6 max-w-[66ch] space-y-2.5">
                  {[
                    "Upload only material you have the right to use. Do not upload material that infringes someone else's copyright.",
                    "Do not include students' personal information in notes you upload for AI generation.",
                    "Subiza uses up to 60,000 characters of notes for each generation. Longer text is cut off, so split large documents into parts.",
                  ].map((line) => (
                    <li key={line} className="relative pl-6 leading-relaxed">
                      <span
                        className="absolute left-1 top-2 h-2 w-2 rounded-[2px] bg-accent"
                        aria-hidden="true"
                      />
                      {line}
                    </li>
                  ))}
                </ul>
                <p className="mb-4 max-w-[66ch] leading-relaxed">
                  You may use, edit and publish the questions Subiza generates for you. Other
                  teachers may receive similar questions, and we do not promise that any question is
                  unique.
                </p>
              </section>

              {/* Section 6 */}
              <section
                id="s6"
                data-term-section
                className="mt-8 scroll-mt-24 border-t border-border pt-10"
              >
                <h2 className="flex items-baseline gap-3.5 font-display text-2xl font-bold tracking-tight text-balance">
                  <span className="min-w-[2.2ch] font-mono text-sm text-accent">6</span>
                  Answers and results
                </h2>
                <p className="mb-4 mt-4 max-w-[66ch] leading-relaxed">
                  When a student submits answers, Subiza records them and, where a question has a
                  defined correct answer, compares them with the answer key the teacher saved.
                  Teachers can see the answers and scores for their own exams.
                </p>
                <p className="mb-4 max-w-[66ch] leading-relaxed">
                  Automatic marking is only as good as the answer key. Check your keys before
                  publishing, including spelling and acceptable alternatives for fill-in-the-blank
                  questions.
                </p>
                <p className="mb-4 max-w-[66ch] leading-relaxed">
                  Teachers remain responsible for final marks and for handling any review request
                  from a student. Subiza is not responsible for grades or decisions made from
                  scores.
                </p>
              </section>

              {/* Section 7 */}
              <section
                id="s7"
                data-term-section
                className="mt-8 scroll-mt-24 border-t border-border pt-10"
              >
                <h2 className="flex items-baseline gap-3.5 font-display text-2xl font-bold tracking-tight text-balance">
                  <span className="min-w-[2.2ch] font-mono text-sm text-accent">7</span>
                  Acceptable use
                </h2>
                <p className="mb-4 mt-4 max-w-[66ch] leading-relaxed">Do not:</p>
                <ul className="mb-6 max-w-[66ch] space-y-2.5">
                  {[
                    "cheat or help others cheat, including sharing exam questions or answers without the teacher's permission",
                    "join an exam you were not invited to, guess PINs, or use another person's identity",
                    "try to reach other users' accounts, exams or data",
                    "upload malicious files, or content that is unlawful, abusive or infringes others' rights",
                    "use bots, scripts or automated tools against Subiza, or try to overload it",
                    "use the AI generator to produce harmful, hateful or sexual content, or try to extract its hidden instructions",
                    "probe, reverse engineer or interfere with the security of the service",
                  ].map((line) => (
                    <li key={line} className="relative pl-6 leading-relaxed">
                      <span
                        className="absolute left-0 top-3 h-px w-3 bg-border"
                        aria-hidden="true"
                      />
                      {line}
                    </li>
                  ))}
                </ul>
              </section>

              {/* Section 8 */}
              <section
                id="s8"
                data-term-section
                className="mt-8 scroll-mt-24 border-t border-border pt-10"
              >
                <h2 className="flex items-baseline gap-3.5 font-display text-2xl font-bold tracking-tight text-balance">
                  <span className="min-w-[2.2ch] font-mono text-sm text-accent">8</span>
                  Data and privacy
                </h2>
                <p className="mb-4 mt-4 max-w-[66ch] leading-relaxed">
                  <strong>What we handle.</strong> For teachers, this is account details, exams,
                  notes and questions. For students, it is the answers they submit, their scores,
                  session timing, and any name or details they enter. We use it to run exams, show
                  results to teachers, and keep the service secure. We do not sell personal
                  information.
                </p>
                <p className="mb-4 max-w-[66ch] leading-relaxed">
                  <strong>Who helps us run Subiza.</strong>
                </p>
                <div className="my-6 overflow-x-auto rounded-xl border border-border bg-surface/80 backdrop-blur-sm">
                  <table className="w-full min-w-[540px] border-collapse text-[15px] leading-normal last:[&_td]:border-b-0">
                    <thead>
                      <tr>
                        <th className="p-3.5 text-left align-top font-mono text-xs uppercase tracking-widest text-muted">
                          Service
                        </th>
                        <th className="p-3.5 text-left align-top font-mono text-xs uppercase tracking-widest text-muted">
                          What it does
                        </th>
                        <th className="p-3.5 text-left align-top font-mono text-xs uppercase tracking-widest text-muted">
                          What it receives
                        </th>
                      </tr>
                    </thead>
                    <tbody className="[&_td]:border-b [&_td]:border-border">
                      <tr>
                        <td className="whitespace-nowrap p-3.5 align-top font-semibold">
                          Supabase
                        </td>
                        <td className="p-3.5 align-top">
                          Database, authentication, live updates during exams
                        </td>
                        <td className="p-3.5 align-top">Accounts, exams, answers, results</td>
                      </tr>
                      <tr>
                        <td className="whitespace-nowrap p-3.5 align-top font-semibold">
                          Cloudflare
                        </td>
                        <td className="p-3.5 align-top">Hosting and delivery of the site</td>
                        <td className="p-3.5 align-top">Traffic to and from Subiza</td>
                      </tr>
                      <tr>
                        <td className="whitespace-nowrap p-3.5 align-top font-semibold">
                          Google Gemini
                        </td>
                        <td className="p-3.5 align-top">Drafts exam questions</td>
                        <td className="p-3.5 align-top">
                          The notes, options and instructions you submit for generation
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <p className="mb-4 max-w-[66ch] leading-relaxed">
                  Google handles notes sent to Gemini under its own terms, which we do not control.
                  This is why section 5 asks you to keep students' personal information out of your
                  notes.
                </p>
                <p className="mb-4 max-w-[66ch] leading-relaxed">
                  <strong>Security.</strong> The key that connects Subiza to Gemini is kept on our
                  servers and is never placed in the code your browser downloads. We take reasonable
                  steps to protect your data, but no online service is perfectly secure.
                </p>
                <p className="mb-4 max-w-[66ch] leading-relaxed">
                  <strong>Your choices.</strong> You can ask us to delete your account and the exams
                  under it using the contact in section 13. We may keep limited records where the
                  law requires. Students who want to see or question their results should ask their
                  teacher, who controls the exam. We handle personal data in line with the data
                  protection law that applies in Rwanda.
                </p>
              </section>

              {/* Section 9 */}
              <section
                id="s9"
                data-term-section
                className="mt-8 scroll-mt-24 border-t border-border pt-10"
              >
                <h2 className="flex items-baseline gap-3.5 font-display text-2xl font-bold tracking-tight text-balance">
                  <span className="min-w-[2.2ch] font-mono text-sm text-accent">9</span>
                  Availability and limits
                </h2>
                <p className="mb-4 mt-4 max-w-[66ch] leading-relaxed">
                  We aim to keep Subiza available, but we do not promise it will run without
                  interruption. Maintenance, outages in services we rely on, or network problems can
                  interrupt an exam. For high-stakes exams, keep a backup plan.
                </p>
                <p className="mb-4 max-w-[66ch] leading-relaxed">
                  AI generation depends on Google's service and its usage limits. At busy times, or
                  when a daily limit is reached, generation may be unavailable for a while. You can
                  write questions by hand or try again later.
                </p>
                <p className="mb-4 max-w-[66ch] leading-relaxed">
                  We may change features, including the AI model behind question generation, from
                  time to time.
                </p>
              </section>

              {/* Section 10 */}
              <section
                id="s10"
                data-term-section
                className="mt-8 scroll-mt-24 border-t border-border pt-10"
              >
                <h2 className="flex items-baseline gap-3.5 font-display text-2xl font-bold tracking-tight text-balance">
                  <span className="min-w-[2.2ch] font-mono text-sm text-accent">10</span>
                  Suspension and ending
                </h2>
                <p className="mb-4 mt-4 max-w-[66ch] leading-relaxed">
                  You can stop using Subiza at any time and ask us to delete your account.
                </p>
                <p className="mb-4 max-w-[66ch] leading-relaxed">
                  We may suspend or remove accounts, exams or content that break these terms, put
                  other people at risk, or harm the service. Where it is reasonable to do so, we
                  will tell you why. Parts of these terms that are meant to continue, such as
                  ownership, disclaimers and limits on liability, stay in effect after your access
                  ends.
                </p>
              </section>

              {/* Section 11 */}
              <section
                id="s11"
                data-term-section
                className="mt-8 scroll-mt-24 border-t border-border pt-10"
              >
                <h2 className="flex items-baseline gap-3.5 font-display text-2xl font-bold tracking-tight text-balance">
                  <span className="min-w-[2.2ch] font-mono text-sm text-accent">11</span>
                  Disclaimers and liability
                </h2>
                <p className="mb-4 mt-4 max-w-[66ch] leading-relaxed">
                  Subiza is provided &ldquo;as is&rdquo; and &ldquo;as available&rdquo;. To the
                  extent the law allows, we do not promise that it will be error-free or
                  uninterrupted, or that AI-generated questions will be accurate.
                </p>
                <p className="mb-4 max-w-[66ch] leading-relaxed">
                  To the extent the law allows, we are not liable for indirect or consequential
                  loss, including lost marks, lost exam time, disputes over results, or decisions
                  made because of exam outcomes. Our total liability for any claim connected with
                  Subiza is limited to the amount you paid us for Subiza in the 12 months before the
                  claim arose.
                </p>
                <p className="mb-4 max-w-[66ch] leading-relaxed">
                  Teachers are responsible for their exams, including the accuracy of the content
                  and the fairness of the marking. Nothing in these terms limits any liability that
                  the law does not allow us to limit.
                </p>
              </section>

              {/* Section 12 */}
              <section
                id="s12"
                data-term-section
                className="mt-8 scroll-mt-24 border-t border-border pt-10"
              >
                <h2 className="flex items-baseline gap-3.5 font-display text-2xl font-bold tracking-tight text-balance">
                  <span className="min-w-[2.2ch] font-mono text-sm text-accent">12</span>
                  Changes to these terms
                </h2>
                <p className="mb-4 mt-4 max-w-[66ch] leading-relaxed">
                  We may update these terms. When we do, we change the date and version at the top
                  of this page, and for significant changes we will show a notice in Subiza. If you
                  keep using Subiza after a change, you accept the updated terms.
                </p>
              </section>

              {/* Section 13 */}
              <section
                id="s13"
                data-term-section
                className="mt-8 scroll-mt-24 border-t border-border pt-10"
              >
                <h2 className="flex items-baseline gap-3.5 font-display text-2xl font-bold tracking-tight text-balance">
                  <span className="min-w-[2.2ch] font-mono text-sm text-accent">13</span>
                  Law and contact
                </h2>
                <p className="mb-4 mt-4 max-w-[66ch] leading-relaxed">
                  These terms are governed by the laws of the Republic of Rwanda. If a disagreement
                  comes up, we will first try to settle it through good-faith discussion. If that
                  does not work, the courts of Rwanda have jurisdiction.
                </p>
                <p className="mb-4 max-w-[66ch] leading-relaxed">
                  For questions about these terms, deletion requests, or reports of misuse, contact
                  us at{" "}
                  <a
                    href={`mailto:${SUPPORT_EMAIL}`}
                    className="font-medium text-accent underline underline-offset-2 hover:underline-offset-4"
                  >
                    {SUPPORT_EMAIL}
                  </a>
                  .
                </p>
              </section>

              {/* Page footer */}
              <footer className="mt-12 flex flex-wrap items-center justify-between gap-2.5 border-t border-border pt-6 text-sm text-muted">
                <span>
                  Subiza Terms and Conditions &middot; Version 1.0 &middot; Effective 9 October 2026
                </span>
                <a
                  href="#top"
                  className="text-muted underline underline-offset-2 hover:text-accent"
                >
                  Back to top
                </a>
              </footer>
            </article>
          </div>
        </div>
      </main>
    </div>
  );
}
